import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getMoodInsights } from '../../lib/api';
import { Link } from 'react-router-dom';
import { SKETCH_PATTERNS } from '../../lib/images';
import {
  Loader2, Flame, BarChart3, Calendar, Sparkles, Info,
  TrendingUp, Crown, ChevronRight
} from 'lucide-react';

export default function MoodInsights() {
  const { deviceId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await getMoodInsights(deviceId);
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  if (!data) return null;

  const { total_logged, streak, graph_data, calendar_matrix, insight_categories, mood_legend, is_premium } = data;

  return (
    <div className="space-y-6" data-testid="mood-insights">
      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <StatCard icon={TrendingUp} iconColor="var(--primary)" label="Total Logged" value={total_logged || 0} />
        <StatCard icon={Flame} iconColor="var(--accent)" label="Streak" value={`${streak || 0}d`} />
        <StatCard icon={BarChart3} iconColor="#3B82F6" label="This Week"
          value={`${(graph_data || []).filter(d => d.mood_id).length}/7`} />
      </div>

      {/* Calendar Heatmap */}
      <div className="rounded-lg p-6 border animate-fade-in"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="mood-calendar-heatmap">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Mood Calendar</h3>
          </div>
          <span className="text-xs" style={{ color: 'var(--muted-fg)' }}>Last 35 days</span>
        </div>
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1.5 mb-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} className="text-center text-xs font-medium py-1" style={{ color: 'var(--muted-fg)' }}>{d}</div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1.5">
          {(calendar_matrix || []).map((day, i) => {
            const mood = (mood_legend || []).find(m => m.id === day.mood_id);
            const isToday = day.date === new Date().toISOString().split('T')[0];
            return (
              <div key={i}
                className="aspect-square rounded-md flex items-center justify-center text-xs font-medium relative transition-all hover:scale-110 cursor-default"
                style={{
                  background: day.mood_id ? (mood?.color || day.color) + '30' : day.color || '#F0F0F0',
                  color: day.mood_id ? (mood?.color || 'var(--text)') : 'var(--muted-fg)',
                  border: isToday ? '2px solid var(--primary)' : 'none',
                }}
                title={day.mood_id ? `${mood?.name || day.mood_id} ${mood?.emoji || ''}` : day.date}
                data-testid={`cal-day-${day.day_num}`}>
                {day.mood_id ? (
                  <span className="text-sm">{mood?.emoji || ''}</span>
                ) : (
                  <span>{day.day_num}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mood Legend */}
      {mood_legend && mood_legend.length > 0 && (
        <div className="rounded-lg p-5 border animate-fade-in"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="mood-legend">
          <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Mood Legend</h3>
          <div className="flex flex-wrap gap-2">
            {mood_legend.map(m => (
              <div key={m.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: `${m.color}15`, color: m.color, border: `1px solid ${m.color}30` }}>
                <span>{m.emoji}</span>
                {m.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insight Categories */}
      {insight_categories && insight_categories.length > 0 && (
        <div className="space-y-4" data-testid="insight-categories">
          {insight_categories.map((cat, i) => (
            <div key={i} className="rounded-lg p-5 border animate-fade-in"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
              data-testid={`insight-cat-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{cat.emoji}</span>
                <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: cat.color || 'var(--text)' }}>{cat.title}</h3>
              </div>
              <div className="space-y-2">
                {(cat.insights || []).map((insight, j) => (
                  <div key={j} className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: cat.color || 'var(--primary)' }} />
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Premium Upsell (if not premium) */}
      {!is_premium && (
        <Link to="/premium" className="block rounded-lg p-5 border animate-fade-in transition-all hover:-translate-y-0.5 hover:shadow-md relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #879B7D10 0%, #E2725B10 100%)', borderColor: 'var(--accent)' + '40' }}
          data-testid="insights-premium-cta">
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: '150px', backgroundPosition: 'right center', backgroundRepeat: 'no-repeat' }} />
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--accent)', color: 'white' }}>
              <Crown className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Unlock Deep Insights</h3>
              <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Get weekly trends, full calendar, and detailed mood patterns with Premium</p>
            </div>
            <ChevronRight className="w-5 h-5 shrink-0" style={{ color: 'var(--accent)' }} />
          </div>
        </Link>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, iconColor, label, value }) {
  return (
    <div className="rounded-lg p-4 border animate-fade-in"
      style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      <Icon className="w-5 h-5 mb-2" style={{ color: iconColor }} />
      <p className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{value}</p>
      <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{label}</p>
    </div>
  );
}
